// IntervalDropdown.jsx
import React, { useState, useEffect } from "react";
import { Box, FormControl, Select, MenuItem, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router-dom';
import { useUserAndGameContext } from "../../../contexts/UserAndGameContext";

const IntervalDropdown = ({ filteredDatesReverse, currentInterval }) => {
  const navigate = useNavigate();
  const { 
    setInterval,
    gameId,
    masterPoolId,
    page,
    gameAddress,
    pickIndex
  } = useUserAndGameContext();
  
  const [selectedInterval, setSelectedInterval] = useState(currentInterval || "");
    
  // Convert array of strings to expected format if needed
  const formattedDates = Array.isArray(filteredDatesReverse) && typeof filteredDatesReverse[0] === 'string' 
    ? filteredDatesReverse.map(dateStr => ({ day: dateStr })) 
    : filteredDatesReverse;
  
  // Format the current interval for display
  const formatIntervalDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = DateTime.fromISO(dateString);
      return date.toFormat("EEE, MMM d"); // Format as "Fri, Apr 18"
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString;
    }
  };

  // IMPORTANT: Use a similar navigation function to MyPicksDateNavBar
  const navigateToInterval = (intervalValue) => {
    // Create a new search params object
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('interval', intervalValue);
    
    // Add other required parameters
    if (masterPoolId) searchParams.set('mId', masterPoolId);
    if (gameId) searchParams.set('gId', gameId);
    if (page) searchParams.set('page', page);
    if (pickIndex) searchParams.set('pickIndex', pickIndex);
    
    // Create the full URL
    const encodedAddress = gameAddress ? encodeURIComponent(gameAddress) : '';
    const basePath = encodedAddress ? `/${encodedAddress}` : window.location.pathname;
    const url = `${basePath}?${searchParams.toString()}`;
        
    // Use the navigate function directly
    navigate(url);
    
    // Also update the context if available
    if (setInterval) {
      setInterval(intervalValue);
    }
  };

  // Handle dropdown change
  const handleIntervalChange = (event) => {
    const newInterval = event.target.value;
    
    setSelectedInterval(newInterval);
    navigateToInterval(newInterval);
  };

  // Initialize on component mount
  useEffect(() => {
    if (currentInterval) {
      setSelectedInterval(currentInterval);
    } else if (formattedDates && formattedDates.length > 0) {
      // If no current interval is set but we have options, use the first one
      const firstInterval = formattedDates[0].day || formattedDates[0];
      setSelectedInterval(firstInterval);
      navigateToInterval(firstInterval);
    }
  }, [currentInterval, formattedDates]);

  // If there are no options, don't render the dropdown
  if (!formattedDates || formattedDates.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1, 
        mb: 2, 
        mt: 2,
        px: 2
      }}>
        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
          Date: {formatIntervalDate(currentInterval)}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: 1, 
      mb: 2, 
      mt: 2,
      px: 2
    }}>
      {/* <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
        Date:
      </Typography> */}
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <Select
          value={selectedInterval}
          onChange={handleIntervalChange}
          displayEmpty
          sx={{ 
            fontWeight: 'bold',
            '& .MuiSelect-select': { py: 1 }
          }}
        >
          {/* Handle the case if filteredDatesReverse is a simple array of strings */}
          {formattedDates.map((dateOption, index) => (
            <MenuItem key={dateOption.day || dateOption || index} value={dateOption.day || dateOption}>
              {dateOption.name || formatIntervalDate(dateOption.day || dateOption)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default IntervalDropdown;